"use client";

import { AnimatePresence, m } from "motion/react";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

import { mergeClasses } from "@trex-2b/libs";

import {
  LandingStepSlideCard,
  type LandingStepSlide,
} from "@/app/landing-page/_components/landing-step-slide-card";

/** Matches `transition.duration` below; lock prevents rapid `AnimatePresence` key thrash. */
const SLIDE_TRANSITION_SEC = 0.38;
const SLIDE_TRANSITION_LOCK_MS = Math.round(SLIDE_TRANSITION_SEC * 1000) + 80;
const SLIDE_AUTO_PLAY_MS = 4500;

/** Figma 8788:9951 + slides 9167:* — hero + step column + raster panel. */
const SLIDES: readonly LandingStepSlide[] = [
  {
    stepLabel: "Step 1",
    heading: "Define your ideal user.",
    description:
      "Set criteria across spending power, engagement depth, social reach, and identity. Your campaign only reaches profiles that clear the bar.",
    image: "/images/landing/steps-carousel/step_1.webp",
  },
  {
    stepLabel: "Step 2",
    heading: "Launch in 48 hours.",
    description:
      "Pick the actions to perform, set your budget, launch. Every participant is verified before they count.",
    image: "/images/landing/steps-carousel/step_2.webp",
  },
  {
    stepLabel: "Step 3",
    heading: "Watch your growth compound.",
    description:
      "Every acquired user comes with a verified profile. Retention, activity, conversion are all visible and auditable.",
    image: "/images/landing/steps-carousel/step_3.webp",
  },
  {
    stepLabel: "Step 4",
    heading: "Read your intelligence report",
    description:
      "Get the full breakdown to grow further: tier distribution, persona splits. The audit your next step will need.",
    image: "/images/landing/steps-carousel/step_4.webp",
  },
];

/** `custom` dir: `+1` next (enters from right), `-1` prev (enters from left). */
const rowVariants = {
  initial: (dir: number) => ({
    x: dir >= 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  animate: { x: 0, opacity: 1 },
  exit: (dir: number) => ({
    x: dir >= 0 ? "-100%" : "100%",
    opacity: 0,
  }),
};

function NavArrowButton({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) {
  const label = direction === "prev" ? "Previous slide" : "Next slide";
  return (
    <m.button
      type="button"
      aria-label={label}
      data-testid={
        direction === "prev"
          ? "landing-steps-carousel-prev-btn"
          : "landing-steps-carousel-next-btn"
      }
      className="pointer-events-auto flex size-[56px] shrink-0 items-center justify-center overflow-hidden border-0 bg-transparent p-0 cursor-pointer"
      onClick={onClick}
      whileTap={{ scale: 0.6 }}
      transition={{ type: "spring", stiffness: 520, damping: 18 }}
    >
      <Image
        src={
          direction === "prev"
            ? "/images/landing/steps-carousel/nav-arrow-prev.svg"
            : "/images/landing/steps-carousel/nav-arrow-next.svg"
        }
        alt=""
        width={56}
        height={56}
        draggable={false}
        className="pointer-events-none block size-[56px] max-h-none max-w-none"
      />
    </m.button>
  );
}

export function LandingStepsViewPager({ className }: { className?: string }) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const count = SLIDES.length;
  const slide = SLIDES[index];

  const transitionLockRef = useRef(false);
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoPlayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const acquireTransitionLock = useCallback(() => {
    if (unlockTimerRef.current !== null) {
      clearTimeout(unlockTimerRef.current);
      unlockTimerRef.current = null;
    }
    transitionLockRef.current = true;
    unlockTimerRef.current = setTimeout(() => {
      transitionLockRef.current = false;
      unlockTimerRef.current = null;
    }, SLIDE_TRANSITION_LOCK_MS);
  }, []);

  useEffect(
    () => () => {
      if (unlockTimerRef.current !== null) {
        clearTimeout(unlockTimerRef.current);
      }
      if (autoPlayTimerRef.current !== null) {
        clearInterval(autoPlayTimerRef.current);
      }
    },
    [],
  );

  const go = useCallback(
    (delta: number) => {
      if (transitionLockRef.current) return;
      acquireTransitionLock();
      setDirection(delta >= 0 ? 1 : -1);
      setIndex((i) => (i + delta + count) % count);
    },
    [acquireTransitionLock, count],
  );

  const goTo = useCallback(
    (i: number) => {
      if (i === index) return;
      if (transitionLockRef.current) return;
      acquireTransitionLock();
      setDirection(i > index ? 1 : -1);
      setIndex(i);
    },
    [acquireTransitionLock, index],
  );

  const stopAutoPlay = useCallback(() => {
    if (autoPlayTimerRef.current === null) return;
    clearInterval(autoPlayTimerRef.current);
    autoPlayTimerRef.current = null;
  }, []);

  const startAutoPlay = useCallback(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    stopAutoPlay();
    autoPlayTimerRef.current = setInterval(() => {
      go(1);
    }, SLIDE_AUTO_PLAY_MS);
  }, [go, stopAutoPlay]);

  const restartAutoPlay = useCallback(() => {
    startAutoPlay();
  }, [startAutoPlay]);

  useEffect(() => {
    startAutoPlay();
    return stopAutoPlay;
  }, [startAutoPlay, stopAutoPlay]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoPlay();
        return;
      }
      startAutoPlay();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [startAutoPlay, stopAutoPlay]);

  return (
    <section
      id="landing-section-how"
      className={mergeClasses(
        "bg-white text-black",
        "w-full py-[159px]",
        "font-inter",
        className,
      )}
    >
      <div className="mx-auto w-[994px]">
        <div
          className={mergeClasses(
            "w-full text-center capitalize text-[44px] font-medium leading-[46px] tracking-[-3.6px] text-[#080808]",
            "font-ancizar",
          )}
        >
          <p>
            From criteria to real users.
            <br aria-hidden />
            In 48 hours.
          </p>
        </div>
      </div>

      <div className="relative mt-[86px] w-full">
        <div className="mx-auto w-[994px]">
          <div className="relative left-[-3px]" aria-roledescription="carousel">
            <div className="relative h-[410px] w-[1000px] overflow-hidden">
              <AnimatePresence custom={direction} initial={false} mode="sync">
                <m.div
                  key={index}
                  role="tabpanel"
                  aria-label={`${slide.stepLabel}: ${slide.heading}`}
                  custom={direction}
                  variants={rowVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{
                    duration: SLIDE_TRANSITION_SEC,
                    ease: [0.32, 0.72, 0, 1],
                  }}
                  className="absolute inset-y-0 left-0 flex w-[1000px] items-center"
                >
                  <LandingStepSlideCard slide={slide} index={index} />
                </m.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* `34109:8330`: 74px gutter on 1512 frame, vertically centered on slide row */}
        <div
          className="pointer-events-none absolute inset-x-[74px] top-0 flex h-[410px] items-center justify-between"
          onMouseEnter={stopAutoPlay}
          onMouseLeave={startAutoPlay}
        >
          <NavArrowButton
            direction="prev"
            onClick={() => {
              go(-1);
              restartAutoPlay();
            }}
          />
          <NavArrowButton
            direction="next"
            onClick={() => {
              go(1);
              restartAutoPlay();
            }}
          />
        </div>
      </div>

      {/* `34109:8325`: flex gap-[16px], 12px circles */}
      <div className="mx-auto mt-[82px] flex w-[994px] justify-center gap-[16px]">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`landing-steps-carousel-dot-${i + 1}-btn`}
            aria-current={i === index ? true : undefined}
            className={mergeClasses(
              "size-[12px] shrink-0 rounded-full border-0 p-0 transition-colors cursor-pointer",
              i === index ? "bg-black" : "bg-[#c4c4c4]",
            )}
            onClick={() => {
              goTo(i);
              restartAutoPlay();
            }}
          />
        ))}
      </div>
    </section>
  );
}
